import { useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import {
  useAdminApiProviders,
  useCreateAdminApiProvider,
  useUpdateAdminApiProvider,
  useDeleteAdminApiProvider,
  useToggleAdminApiProviderStatus,
  useAdminProviderServices,
  useCreateAdminProviderService,
  useUpdateAdminProviderService,
  useDeleteAdminProviderService,
  useToggleAdminProviderServiceStatus,
} from "../../../api/admin";

export default function APIProvidersManagement() {
  const [activeTab, setActiveTab] = useState<"providers" | "services">(
    "providers",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [showApiKey, setShowApiKey] = useState<number | null>(null);

  // Providers Hooks
  const { data: providersData, isLoading: providersLoading } =
    useAdminApiProviders();
  const createProvider = useCreateAdminApiProvider();
  const updateProvider = useUpdateAdminApiProvider();
  const deleteProvider = useDeleteAdminApiProvider();
  const toggleProviderStatus = useToggleAdminApiProviderStatus();

  // Services Hooks
  const { data: servicesData, isLoading: servicesLoading } =
    useAdminProviderServices();
  const createService = useCreateAdminProviderService();
  const updateService = useUpdateAdminProviderService();
  const deleteService = useDeleteAdminProviderService();
  const toggleServiceStatus = useToggleAdminProviderServiceStatus();

  const providers = providersData?.results?.data || [];
  const services = servicesData?.results?.data || [];

  const [providerForm, setProviderForm] = useState({
    name: "",
    base_url: "",
    api_key: "",
    auth_type: "bearer",
    rate_limit_per_minute: 60,
    priority: 1,
    is_active: true,
  });

  const [serviceForm, setServiceForm] = useState({
    provider: "",
    name: "airtime",
    endpoint: "",
    method: "POST",
    required_params: [] as string[],
    payload_mapping: "{}",
    is_active: true,
  });

  const filteredProviders = useMemo(() => {
    return providers.filter(
      (provider: any) =>
        provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.base_url.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [providers, searchTerm]);

  const filteredServices = useMemo(() => {
    return services.filter(
      (service: any) =>
        service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.provider_name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [services, searchTerm]);

  const handleProviderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await updateProvider.mutateAsync({
          id: editingItem.id,
          data: providerForm,
        });
        toast.success("Provider updated successfully");
      } else {
        await createProvider.mutateAsync(providerForm);
        toast.success("Provider created successfully");
      }
      resetForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Operation failed");
    }
  };

  const handleServiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...serviceForm,
        payload_mapping: JSON.parse(serviceForm.payload_mapping || "{}"),
        required_params:
          typeof serviceForm.required_params === "string"
            ? (serviceForm.required_params as string)
                .split(",")
                .map((s) => s.trim())
            : serviceForm.required_params,
      };

      if (editingItem) {
        await updateService.mutateAsync({ id: editingItem.id, data: payload });
        toast.success("Service updated successfully");
      } else {
        await createService.mutateAsync(payload);
        toast.success("Service created successfully");
      }
      resetForm();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Operation failed");
    }
  };

  const resetForm = () => {
    setProviderForm({
      name: "",
      base_url: "",
      api_key: "",
      auth_type: "bearer",
      rate_limit_per_minute: 60,
      priority: 1,
      is_active: true,
    });
    setServiceForm({
      provider: "",
      name: "airtime",
      endpoint: "",
      method: "POST",
      required_params: [],
      payload_mapping: "{}",
      is_active: true,
    });
    setEditingItem(null);
    setShowModal(false);
  };

  const handleEditProvider = (provider: any) => {
    setEditingItem(provider);
    setProviderForm({
      name: provider.name,
      base_url: provider.base_url,
      api_key: provider.api_key || "",
      auth_type: provider.auth_type,
      rate_limit_per_minute: provider.rate_limit_per_minute,
      priority: provider.priority,
      is_active: provider.is_active,
    });
    setShowModal(true);
  };

  const handleEditService = (service: any) => {
    setEditingItem(service);
    setServiceForm({
      provider: service.provider,
      name: service.name,
      endpoint: service.endpoint,
      method: service.method,
      required_params: service.required_params,
      payload_mapping: JSON.stringify(service.payload_mapping, null, 2),
      is_active: service.is_active,
    });
    setShowModal(true);
  };

  const handleDeleteProvider = async (id: number) => {
    if (confirm("Are you sure? This will delete all associated services.")) {
      try {
        await deleteProvider.mutateAsync(id);
        toast.success("Provider deleted successfully");
      } catch (error) {
        toast.error("Failed to delete provider");
      }
    }
  };

  const handleDeleteService = async (id: number) => {
    if (confirm("Are you sure you want to delete this service?")) {
      try {
        await deleteService.mutateAsync(id);
        toast.success("Service deleted successfully");
      } catch (error) {
        toast.error("Failed to delete service");
      }
    }
  };

  const maskApiKey = (key: string) => {
    if (!key) return "No API Key";
    if (key.length <= 8) return key;
    return key.substring(0, 4) + "****" + key.substring(key.length - 4);
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            API Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage third-party providers and their services
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="mt-4 md:mt-0 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-fill mr-2"></i>
          Add {activeTab === "providers" ? "Provider" : "Service"}
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("providers")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "providers"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            API Providers
          </button>
          <button
            onClick={() => setActiveTab("services")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "services"
                ? "border-red-500 text-red-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Provider Services
          </button>
        </nav>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="max-w-md">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Search ${activeTab}...`}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === "providers" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {providersLoading ? (
            <div className="col-span-2 text-center py-12">
              <i className="ri-loader-4-line animate-spin text-3xl text-red-600"></i>
            </div>
          ) : (
            filteredProviders.map((provider: any) => (
              <div
                key={provider.id}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <i className="ri-server-fill text-cyan-600 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {provider.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {provider.services_count} Services
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => toggleProviderStatus.mutate(provider.id)}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        provider.is_active ? "bg-purple-500" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform absolute top-0.5 ${
                          provider.is_active
                            ? "translate-x-6"
                            : "translate-x-0.5"
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>

                <div className="space-y-3 mb-4 text-sm">
                  <div>
                    <span className="text-gray-500">Base URL:</span>
                    <p className="font-medium text-gray-900 break-all">
                      {provider.base_url}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">API Key:</span>
                    <div className="flex items-center space-x-2">
                      <p className="font-mono text-gray-900">
                        {showApiKey === provider.id
                          ? provider.api_key
                          : maskApiKey(provider.api_key)}
                      </p>
                      <button
                        onClick={() =>
                          setShowApiKey(
                            showApiKey === provider.id ? null : provider.id,
                          )
                        }
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <i
                          className={`ri-${showApiKey === provider.id ? "eye-off" : "eye"}-fill`}
                        ></i>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-gray-500">Auth Type:</span>
                      <p className="font-medium text-gray-900 capitalize">
                        {provider.auth_type}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Rate Limit:</span>
                      <p className="font-medium text-gray-900">
                        {provider.rate_limit_per_minute}/min
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditProvider(provider)}
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                  >
                    <i className="ri-edit-fill mr-1"></i> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteProvider(provider.id)}
                    className="flex-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm"
                  >
                    <i className="ri-delete-bin-fill mr-1"></i> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Provider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Endpoint
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {servicesLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <i className="ri-loader-4-line animate-spin text-2xl text-red-600"></i>
                    </td>
                  </tr>
                ) : (
                  filteredServices.map((service: any) => (
                    <tr key={service.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full capitalize">
                          {service.name.replace("_", " ")}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {service.provider_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                        {service.endpoint}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded border ${
                            service.method === "GET"
                              ? "bg-purple-50 text-purple-700 border-purple-200"
                              : service.method === "POST"
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-orange-50 text-orange-700 border-orange-200"
                          }`}
                        >
                          {service.method}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleServiceStatus.mutate(service.id)}
                          className={`w-8 h-4 rounded-full transition-colors relative ${
                            service.is_active ? "bg-purple-500" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`w-3 h-3 bg-white rounded-full shadow-sm transform transition-transform absolute top-0.5 ${
                              service.is_active
                                ? "translate-x-4.5"
                                : "translate-x-0.5"
                            }`}
                          ></div>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditService(service)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <i className="ri-edit-fill"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteService(service.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <i className="ri-delete-bin-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingItem ? "Edit" : "Add"}{" "}
                  {activeTab === "providers" ? "Provider" : "Service"}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-fill text-xl"></i>
                </button>
              </div>

              <form
                onSubmit={
                  activeTab === "providers"
                    ? handleProviderSubmit
                    : handleServiceSubmit
                }
                className="space-y-4"
              >
                {activeTab === "providers" ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={providerForm.name}
                        onChange={(e) =>
                          setProviderForm({
                            ...providerForm,
                            name: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Base URL
                      </label>
                      <input
                        type="url"
                        value={providerForm.base_url}
                        onChange={(e) =>
                          setProviderForm({
                            ...providerForm,
                            base_url: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key
                      </label>
                      <input
                        type="text"
                        value={providerForm.api_key}
                        onChange={(e) =>
                          setProviderForm({
                            ...providerForm,
                            api_key: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 font-mono"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Auth Type
                        </label>
                        <select
                          value={providerForm.auth_type}
                          onChange={(e) =>
                            setProviderForm({
                              ...providerForm,
                              auth_type: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        >
                          <option value="bearer">Bearer Token</option>
                          <option value="token">Token</option>
                          <option value="oauth">OAuth</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Rate Limit (/min)
                        </label>
                        <input
                          type="number"
                          value={providerForm.rate_limit_per_minute}
                          onChange={(e) =>
                            setProviderForm({
                              ...providerForm,
                              rate_limit_per_minute: Number(e.target.value),
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Provider
                      </label>
                      <select
                        value={serviceForm.provider}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            provider: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        required
                      >
                        <option value="">Select Provider</option>
                        {providers.map((p: any) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Service Type
                        </label>
                        <select
                          value={serviceForm.name}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              name: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        >
                          <option value="airtime">Airtime</option>
                          <option value="data">Data</option>
                          <option value="bill">Bill Payment</option>
                          <option value="cable">Cable TV</option>
                          <option value="exam">Exam Pin</option>
                          <option value="airtime_pin">Airtime Card</option>
                          <option value="data_pin">Data Card</option>
                          <option value="verify_cable">Verify Cable</option>
                          <option value="verify_meter">Verify Meter</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Method
                        </label>
                        <select
                          value={serviceForm.method}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              method: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Endpoint
                      </label>
                      <input
                        type="text"
                        value={serviceForm.endpoint}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            endpoint: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 font-mono"
                        placeholder="/api/v1/service"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Required Parameters
                        <span className="text-xs text-gray-500 ml-2">
                          Comma-separated (e.g. network, amount)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={
                          Array.isArray(serviceForm.required_params)
                            ? serviceForm.required_params.join(", ")
                            : serviceForm.required_params
                        }
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            required_params: e.target.value
                              .split(",")
                              .map((s) => s.trim()),
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 font-mono text-sm"
                        placeholder="network, amount, phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payload Mapping (JSON)
                        <span className="text-xs text-gray-500 ml-2">
                          Use {"{{value}}"} for dynamic fields
                        </span>
                      </label>
                      <textarea
                        value={serviceForm.payload_mapping}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            payload_mapping: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 font-mono text-sm h-32"
                        placeholder='{"network": "{{network}}", "amount": "{{amount}}"}'
                      />
                    </div>
                  </>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    {editingItem ? "Update" : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
